import { useQuery } from '@tanstack/react-query';
import type {
  CustomersDrilldownPage,
  CustomersDrilldownQuery,
  CustomersReport,
  CustomersReportQuery,
  OpticalDrilldownPage,
  OpticalDrilldownQuery,
  OpticalReport,
  OpticalReportQuery,
} from '@optiflow/types';
import { api } from '@/services/apiClient';

export const customersOpticalKeys = {
  customers: (query: CustomersReportQuery) => ['reports', 'customers', query] as const,
  customersRecords: (query: CustomersDrilldownQuery | Record<string, never>) =>
    ['reports', 'customers', 'records', query] as const,
  optical: (query: OpticalReportQuery) => ['reports', 'optical', query] as const,
  opticalRecords: (query: OpticalDrilldownQuery | Record<string, never>) =>
    ['reports', 'optical', 'records', query] as const,
};

/** Drops empty values so the server resolves its own defaults rather than reading "". */
function cleanQuery<T extends object>(query: T): T {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
  ) as T;
}

/**
 * The Customers tab. Registrations always; spending and balances only when the server authorised
 * them — an absent section is one this account may not see, never a zero.
 */
export function useCustomersReportQuery(query: CustomersReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: customersOpticalKeys.customers(cleanQuery(query)),
    queryFn: () => api.get<CustomersReport>('/reports/customers', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The records behind one Customers figure, paged and totalled by the server. */
export function useCustomersRecordsQuery(query: CustomersDrilldownQuery | null) {
  return useQuery({
    queryKey: customersOpticalKeys.customersRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<CustomersDrilldownPage>('/reports/customers/records', {
        params: cleanQuery(query as CustomersDrilldownQuery),
      }),
    enabled: query !== null,
  });
}

/** The Optical tab: current jobs as of now, and lifecycle history for the period. */
export function useOpticalReportQuery(query: OpticalReportQuery, { enabled = true } = {}) {
  return useQuery({
    queryKey: customersOpticalKeys.optical(cleanQuery(query)),
    queryFn: () => api.get<OpticalReport>('/reports/optical', { params: cleanQuery(query) }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The jobs behind one Optical figure, paged by the server. */
export function useOpticalRecordsQuery(query: OpticalDrilldownQuery | null) {
  return useQuery({
    queryKey: customersOpticalKeys.opticalRecords(query ? cleanQuery(query) : {}),
    queryFn: () =>
      api.get<OpticalDrilldownPage>('/reports/optical/records', {
        params: cleanQuery(query as OpticalDrilldownQuery),
      }),
    enabled: query !== null,
  });
}
